/**
 * Tauri bindings for anonymous-access credential storage (OS keychain).
 *
 * The Rust IPC DTO uses `#[serde(rename_all = "camelCase")]`, so tokens pass
 * through as {@link AnonymousAccessToken} without wire mapping.
 */
import { isTauri } from '@/tauri/utils';
import { type AnonymousAccessToken } from '@heidi/dictate';
import { invoke } from '@tauri-apps/api/core';

const COMMAND_PREFIX = 'dictation_';

/** Persist the anonymous-access credential to the OS keychain. */
export async function saveAnonymousAccess(token: AnonymousAccessToken): Promise<void> {
  if (!isTauri()) return;
  await invoke(`${COMMAND_PREFIX}save_anonymous_access`, { data: token });
}

/** Load the anonymous-access credential from the keychain, if present. */
export async function loadAnonymousAccess(): Promise<AnonymousAccessToken | null> {
  if (!isTauri()) return null;
  return invoke<AnonymousAccessToken | null>(`${COMMAND_PREFIX}load_anonymous_access`);
}

/** Clear the anonymous-access credential (on sign-in or explicit logout). */
export async function clearAnonymousAccess(): Promise<void> {
  if (!isTauri()) return;
  await invoke(`${COMMAND_PREFIX}clear_anonymous_access`);
}

/**
 * Invoke a native command that returns an anonymous credential. These routes
 * are desktop-only (the webview can't reach the transcription service
 * directly), so off-Tauri is a hard error rather than a silent no-op.
 */
async function invokeForToken(
  command: string,
  action: string,
  args: Record<string, unknown>
): Promise<AnonymousAccessToken> {
  if (!isTauri()) {
    throw new Error(`Native anonymous access ${action} requires Tauri`);
  }
  return invoke<AnonymousAccessToken>(`${COMMAND_PREFIX}${command}`, args);
}

/** Bootstrap an anonymous token via native Rust HTTP (desktop only). */
export async function bootstrapAnonymousAccessToken(
  baseUrl: string,
  dictationContext?: 'onboarding'
): Promise<AnonymousAccessToken> {
  return invokeForToken('bootstrap_anonymous_access', 'bootstrap', {
    baseUrl,
    dictationContext: dictationContext ?? null,
  });
}

/** Refresh an anonymous token via native Rust HTTP (desktop only). */
export async function refreshAnonymousAccessToken(
  baseUrl: string,
  currentToken: string,
  dictationContext?: 'onboarding'
): Promise<AnonymousAccessToken> {
  return invokeForToken('refresh_anonymous_access', 'refresh', {
    baseUrl,
    currentToken,
    dictationContext: dictationContext ?? null,
  });
}
